/**
 * Title + description for routes whose React page component is shared across
 * several SEO paths (DirectorHome serves 5 paths, DownloadApp serves 2).
 *
 * Why this exists: the server renders per-route SEO from ROUTE_SEO in
 * server/_core/seo.ts, but a shared component calls useSEO() with one
 * hardcoded title, which overwrites the server's <title>/description on
 * hydration. Crawlers that don't run JS keep the correct SSR head, but Google
 * renders JS — so it saw every one of these routes carrying the shared
 * component's title (e.g. /ai-music-video-generator presenting the homepage
 * title), which reads as duplicate titles across the cluster.
 *
 * Both sides import this map, so the hydrated <head> matches the SSR <head>
 * and the two can't drift. Server: ROUTE_SEO spreads these values. Client:
 * DirectorHome / DownloadApp look up the current path and pass the result to
 * useSEO.
 *
 * Only paths where the shared component would otherwise clobber a distinct
 * server title belong here. "/" is intentionally absent: its SSR title already
 * equals DirectorHome's default, so there is nothing to correct.
 */
export type SharedComponentRouteSeo = {
  title: string;
  description: string;
};

export const SHARED_COMPONENT_ROUTE_SEO: Record<string, SharedComponentRouteSeo> = {
  "/ai-music-video-generator": {
    title: "AI Music Video Generator — Turn Any Song into a Video Free | Solmi",
    description:
      "Solmi's AI music video generator turns any song, Suno track, or MP3 into a finished music video with auto-lyrics, beat-synced visuals, and TikTok / YouTube / Reels formats. Free to start.",
  },
  "/free-ai-music-video-generator": {
    title: "Free AI Music Video Generator — No Card, 1080p Export | Solmi AI",
    description:
      "Generate AI music videos free with Solmi. Daily free credits, no credit card, full-song length, 1080p export, and lyric overlays. Works with Suno, Udio, and any MP3.",
  },
  "/suno-to-video": {
    title: "Suno to Video — Turn Suno Songs into Music Videos | Solmi",
    description:
      "Paste your Suno share link and Solmi turns the song into a finished music video with auto-detected lyrics, beat-synced visuals, and YouTube / TikTok / Reels aspect ratios. Free to start.",
  },
  "/ai-music-video-director": {
    title:
      "AI Music Video Director — Storyboard, Direct & Render Full Music Videos | Solmi",
    description:
      "Solmi is the AI tool that directs, storyboards, and renders music videos: it analyzes your song, writes a creative brief, builds a shot-by-shot storyboard with consistent characters, then renders and lip-syncs the finished video. Approve references, regenerate any shot. Free to start.",
  },
  "/ai-music-video-workflow": {
    title:
      "End-to-End AI Music Video Workflow — Song to Finished Video in One Tool | Solmi",
    description:
      "Solmi handles the full music video workflow end-to-end: song analysis, creative brief, storyboard, character references, rendering, lip-sync, and platform-ready export — one tool instead of a five-tab pipeline of separate AI apps. Free to start.",
  },
  "/ai-music-video-app": {
    title:
      "AI Music Video App for iOS & Android — Make Music Videos on Your Phone | Solmi",
    description:
      "Solmi is the AI music video maker that supports both iOS and Android, with native apps on the App Store and Google Play plus a full web app. Upload a song or paste a Suno link and render a finished, lip-synced music video from your phone. Free to start.",
  },
};

/**
 * Look up the SEO for a path that may carry a language prefix (/es/foo).
 * `languages` is passed in by the caller so this module stays free of both the
 * server's and the client's language-list imports.
 */
export function getSharedComponentRouteSeo(
  pathname: string,
  languages: readonly string[],
): SharedComponentRouteSeo | null {
  const withoutQuery = pathname.split("?")[0]?.split("#")[0] ?? "/";
  const parts = withoutQuery.split("/").filter(Boolean);
  const basePath = languages.includes(parts[0] ?? "")
    ? `/${parts.slice(1).join("/")}`
    : `/${parts.join("/")}`;
  return SHARED_COMPONENT_ROUTE_SEO[basePath] ?? null;
}
